import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Plus, Send } from "lucide-react";
import Reveal, { SectionHead } from "@/components/Reveal";

const EASE = [0.16, 1, 0.3, 1];

const QUESTIONS = [
    "Where did my money go this month?",
    "Which category spiked compared to last month?",
    "Who are my top 3 merchants this month?",
];

const SNAPSHOT = [
    { label: "Spent", value: "₹12,480", cls: "text-white" },
    { label: "Income", value: "₹45,000", cls: "text-fynary-lime" },
    { label: "Savings", value: "₹32,520", cls: "text-fynary-lime", sub: "72% rate" },
    { label: "Largest", value: "₹6,200", cls: "text-white", sub: "Avg ₹940" },
];

const TREND = [
    { m: "May", v: 38 },
    { m: "Jun", v: 55 },
    { m: "Jul", v: 30 },
    { m: "Aug", v: 62 },
    { m: "Sep", v: 48 },
    { m: "Oct", v: 72 },
];

const BANKS = ["HDFC", "SBI", "ICICI", "Axis", "Kotak"];

function AssistantCard() {
    const [sent, setSent] = useState(null);
    const [thinking, setThinking] = useState(false);

    const ask = (q) => {
        if (thinking) return;
        setSent(q);
        setThinking(true);
        setTimeout(() => setThinking(false), 900);
    };

    return (
        <div
            data-testid="bento-card-assistant"
            className="flex h-full flex-col rounded-[24px] border border-white/10 bg-fynary-surface p-6 sm:p-8"
        >
            <h3 className="font-display text-xl font-bold tracking-tight text-white sm:text-2xl">Ask fynary.</h3>
            <p className="mt-2 text-sm leading-relaxed text-fynary-muted sm:text-base">
                Your own AI chatbot for money — ask about your transactions and it answers using only your own data, month by month,
                category by category.
            </p>

            <div className="mt-6 flex-1 rounded-2xl border border-white/10 bg-fynary-bg p-4 sm:p-5">
                <div className="rounded-xl bg-fynary-surface p-4">
                    <p className="font-display text-lg font-extrabold text-fynary-lime">?</p>
                    <p className="mt-1 text-sm font-bold text-white">Hi, I'm fynary's assistant</p>
                    <p className="text-xs leading-relaxed text-fynary-muted">I only use your own data to answer. Try one of these:</p>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                    {QUESTIONS.map((q, i) => (
                        <button
                            key={q}
                            data-testid={`assistant-chip-${i}`}
                            onClick={() => ask(q)}
                            className={`rounded-full border px-3.5 py-2 text-left text-xs transition-[border-color,background-color,color] duration-200 sm:text-[13px] ${
                                sent === q
                                    ? "border-fynary-lime bg-fynary-lime/10 text-fynary-lime"
                                    : "border-white/10 bg-fynary-surface text-white hover:border-fynary-lime/50"
                            }`}
                        >
                            {q}
                        </button>
                    ))}
                </div>

                <AnimatePresence>
                    {sent && (
                        <motion.div
                            key={sent}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.35, ease: EASE }}
                            className="mt-4 space-y-2"
                        >
                            <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-fynary-lime px-4 py-2.5 text-xs font-medium text-fynary-bg sm:text-sm">
                                {sent}
                            </div>
                            <div className="w-fit max-w-[85%] rounded-2xl rounded-bl-md bg-fynary-surface px-4 py-2.5 text-xs text-white sm:text-sm">
                                {thinking ? (
                                    <span className="flex items-center gap-1.5">
                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-fynary-lime [animation-delay:0ms]" />
                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-fynary-lime [animation-delay:120ms]" />
                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-fynary-lime [animation-delay:240ms]" />
                                    </span>
                                ) : (
                                    "Find me in the app — I answer from your own imported statements."
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

function CsvCard() {
    return (
        <div
            data-testid="bento-card-csv-import"
            className="flex h-full flex-col rounded-[24px] border border-white/10 bg-fynary-surface p-6 sm:p-8"
        >
            <h3 className="font-display text-xl font-bold tracking-tight text-white sm:text-2xl">Private CSV import.</h3>
            <p className="mt-2 text-sm leading-relaxed text-fynary-muted sm:text-base">
                No bank linking. You export the statement, fynary reads it — nothing connects to your account.
            </p>

            <button
                data-testid="csv-upload-dropzone-button"
                className="group mt-6 flex-1 rounded-3xl border border-dashed border-white/15 bg-fynary-bg px-6 py-10 text-center transition-[border-color,background-color,transform] duration-200 hover:border-fynary-lime/60 hover:bg-fynary-lime/5 active:scale-[0.99]"
            >
                <p className="font-display text-lg font-bold text-fynary-lime sm:text-xl">Tap to choose a CSV</p>
                <p className="mt-2 text-sm text-fynary-muted">Bank statement from HDFC, SBI, ICICI, Axis, Kotak…</p>
            </button>

            <p className="mt-4 text-xs text-fynary-muted">Max 5 MB · .csv only · Private to you</p>
            <div className="mt-3 flex flex-wrap gap-2">
                {BANKS.map((b) => (
                    <span
                        key={b}
                        className="rounded-full border border-white/10 bg-fynary-bg px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-fynary-muted"
                    >
                        {b}
                    </span>
                ))}
            </div>
        </div>
    );
}

function SnapshotCard() {
    return (
        <div
            data-testid="bento-card-snapshot"
            className="flex h-full flex-col rounded-[24px] border border-white/10 bg-fynary-surface p-6 sm:p-8"
        >
            <h3 className="font-display text-xl font-bold tracking-tight text-white sm:text-2xl">Your month at a glance.</h3>
            <p className="mt-2 text-sm leading-relaxed text-fynary-muted sm:text-base">
                Spent, income, savings rate and your largest expense — side by side, straight from your own statements.
            </p>

            <div className="mt-6 grid flex-1 grid-cols-2 gap-2.5">
                {SNAPSHOT.map((s) => (
                    <div key={s.label} className="rounded-2xl border border-white/10 bg-fynary-bg p-4">
                        <p className="text-xs text-fynary-muted">{s.label}</p>
                        <p className={`mt-1 font-display text-lg font-bold sm:text-xl ${s.cls}`}>{s.value}</p>
                        {s.sub ? <p className="mt-0.5 text-[11px] text-fynary-muted">{s.sub}</p> : null}
                    </div>
                ))}
            </div>
            <p className="mt-3 text-[11px] text-fynary-muted/70">Sample data — yours comes from your own imports.</p>
        </div>
    );
}

function TrendCard() {
    return (
        <div
            data-testid="bento-card-trends"
            className="flex h-full flex-col rounded-[24px] border border-white/10 bg-fynary-surface p-6 sm:p-8"
        >
            <h3 className="font-display text-xl font-bold tracking-tight text-white sm:text-2xl">Six-month trends.</h3>
            <p className="mt-2 text-sm leading-relaxed text-fynary-muted sm:text-base">
                Watch the last six months side by side and spot where your money actually goes.
            </p>

            <div className="mt-6 flex flex-1 items-end gap-3 sm:gap-4">
                {TREND.map((d, i) => (
                    <div key={d.m} className="group relative flex h-40 flex-1 flex-col justify-end sm:h-48">
                        <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 rounded-full bg-fynary-surface2 px-2.5 py-1 text-[10px] font-semibold text-fynary-lime opacity-0 transition-[opacity] duration-200 group-hover:opacity-100">
                            ₹{d.v}k
                        </span>
                        <motion.div
                            initial={{ height: 0 }}
                            whileInView={{ height: `${d.v}%` }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.8, ease: EASE, delay: i * 0.06 }}
                            className="w-full rounded-lg bg-fynary-lime/80 transition-[background-color] duration-200 group-hover:bg-fynary-lime"
                        />
                        <span className="mt-2 text-center text-[10px] text-fynary-muted sm:text-xs">{d.m}</span>
                    </div>
                ))}
            </div>
            <p className="mt-3 text-[11px] text-fynary-muted/70">Sample data — your chart is built from your own imports.</p>
        </div>
    );
}

function ManualCard() {
    const [added, setAdded] = useState(false);

    const add = () => {
        if (added) return;
        setAdded(true);
        setTimeout(() => setAdded(false), 1400);
    };

    return (
        <div
            data-testid="bento-card-manual"
            className="grid h-full grid-cols-1 gap-8 rounded-[24px] border border-white/10 bg-fynary-surface p-6 sm:p-8 lg:grid-cols-2 lg:items-center"
        >
            <div>
                <h3 className="font-display text-xl font-bold tracking-tight text-white sm:text-2xl">Log it by hand.</h3>
                <p className="mt-2 text-sm leading-relaxed text-fynary-muted sm:text-base">
                    Not everything goes through the bank. fynary's manual transaction button lets you add spending yourself — cash,
                    splits, anything a statement misses.
                </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-fynary-bg p-4 sm:p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-fynary-muted">New transaction</p>
                <div className="mt-3 flex items-center justify-between rounded-2xl border border-white/10 bg-fynary-surface px-4 py-3">
                    <span className="text-sm text-fynary-muted">Amount</span>
                    <span className="text-sm font-bold text-white">₹450</span>
                </div>
                <div className="mt-2 flex items-center justify-between rounded-2xl border border-white/10 bg-fynary-surface px-4 py-3">
                    <span className="text-sm text-fynary-muted">Title</span>
                    <span className="text-sm text-white">Lunch</span>
                </div>
                <button
                    data-testid="manual-add-button"
                    onClick={add}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-fynary-lime px-6 py-3 font-display text-sm font-semibold text-fynary-bg transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.98]"
                >
                    {added ? (
                        <>
                            <Check className="h-4 w-4" />
                            Added to October
                        </>
                    ) : (
                        <>
                            <Plus className="h-4 w-4" />
                            Add transaction
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

export default function Features() {
    return (
        <section id="features" className="scroll-mt-24 py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                <SectionHead
                    eyebrow="Features"
                    title="What fynary. actually does."
                    sub="No syncing, no guesswork. fynary. works from the statement CSVs you import — and answers with your own numbers."
                    testid="features-heading"
                />

                <div className="mt-12 grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7" delay={0.05}>
                        <AssistantCard />
                    </Reveal>
                    <Reveal className="lg:col-span-5" delay={0.12}>
                        <CsvCard />
                    </Reveal>
                    <Reveal className="lg:col-span-5" delay={0.05}>
                        <SnapshotCard />
                    </Reveal>
                    <Reveal className="lg:col-span-7" delay={0.12}>
                        <TrendCard />
                    </Reveal>
                    <Reveal className="lg:col-span-12" delay={0.05}>
                        <ManualCard />
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
